import React from "react";
import { Button, Tooltip } from "@material-ui/core";
import { css } from "@emotion/css";
import { APIError } from "api";
import { useHistory } from "react-router-dom";
import { CataloguePath } from "routes/createContent/paths";
import { Colors } from "theme";
import RolloutStatusChip from "routes/healthForms/components/RolloutStatusChip";
import { deployAttribution, groupLabel } from "routes/healthForms/rolloutText";
import {
  CatalogueGroupRollout,
  CatalogueKind,
  CatalogueRollout,
  useDeployCatalogueEntry,
  useRetireCatalogueEntry,
} from "routes/createContent/api/catalogueDeployments";

const groupTooltip = (entry: CatalogueGroupRollout) => {
  if (entry.status === "inactive") return `Not deployed to ${groupLabel(entry.group)}`;
  const attribution = deployAttribution(entry);
  if (entry.status === "retiring") return `Retired ${attribution} — waiting for environments to converge`;
  if (entry.status === "retired") return `Withdrawn from every ${groupLabel(entry.group)} environment — ${attribution}`;
  if (entry.status === "activating") return `Deployed ${attribution} — waiting for environments to converge`;
  return entry.has_undeployed_changes
    ? `${groupLabel(entry.group)} is serving the previously deployed state, deployed ${attribution}`
    : `Live on every ${groupLabel(entry.group)} environment — deployed ${attribution}`;
};

const servingStaleState = (entry: CatalogueGroupRollout | undefined) =>
  entry?.status === "active" && entry.has_undeployed_changes;

const changesArea = (kind: CatalogueKind) => {
  switch (kind) {
    case "exercise":
      return CataloguePath.Exercises_Changes;
    case "lesson":
      return CataloguePath.Lessons_Changes;
    case "template":
      return CataloguePath.Templates_Changes;
  }
};

const changesPath = (kind: CatalogueKind, name: string, group: string) =>
  changesArea(kind).replace(":name", encodeURIComponent(name)).replace(":group", group);

export const CatalogueDeploymentRow: React.VFC<{
  kind: CatalogueKind;
  name: string;
  rollout: CatalogueRollout | undefined;
  compact?: boolean;
}> = ({ kind, name, rollout, compact = false }) => {
  const deploy = useDeployCatalogueEntry();
  const retire = useRetireCatalogueEntry();
  const history = useHistory();

  if (!rollout) {
    return null;
  }

  const prod = rollout.groups.find(entry => entry.group === "prod");
  const prodLive = prod?.status === "active" || prod?.status === "activating";
  const busy = deploy.isLoading || retire.isLoading;
  const failure = deploy.error ?? retire.error;

  return (
    <div className={compact ? `${styles.row} ${styles.compactRow}` : styles.row}>
      {rollout.groups.map(entry => {
        const stale = servingStaleState(entry);
        const chip = (
          <RolloutStatusChip
            status={entry.status}
            label={groupLabel(entry.group)}
            tooltip={stale ? `${groupTooltip(entry)} — click to review` : groupTooltip(entry)}
            stale={stale}
          />
        );
        return stale ? (
          <button
            key={entry.group}
            type="button"
            className={styles.chipButton}
            onClick={event => {
              event.stopPropagation();
              history.push(changesPath(kind, name, entry.group));
            }}
          >
            {chip}
          </button>
        ) : (
          <span key={entry.group}>{chip}</span>
        );
      })}
      {!compact && prod?.deployable && (
        <span className={styles.action} onClick={event => event.stopPropagation()}>
          {prodLive ? (
            <>
              {servingStaleState(prod) && (
                <Tooltip title="Release the edits made since the last prod deploy">
                  <Button
                    size="small"
                    color="primary"
                    disabled={busy}
                    onClick={() => deploy.mutate({ kind, name, group: "prod" })}
                  >
                    Deploy to prod
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Withdraw from prod — every prod environment stops serving it on its next sync">
                <Button size="small" disabled={busy} onClick={() => retire.mutate({ kind, name, group: "prod" })}>
                  Retire from prod
                </Button>
              </Tooltip>
            </>
          ) : (
            <Tooltip title={`Release this ${kind}'s current state to prod`}>
              <Button
                size="small"
                color="primary"
                disabled={busy}
                onClick={() => deploy.mutate({ kind, name, group: "prod" })}
              >
                Deploy to prod
              </Button>
            </Tooltip>
          )}
        </span>
      )}
      {failure && (
        <span className={styles.failure} title={errorMessage(failure)}>
          {errorMessage(failure)}
        </span>
      )}
    </div>
  );
};

const errorMessage = (error: unknown): string => {
  try {
    const parsed = JSON.parse((error as Error).message) as APIError;
    return parsed.details?.[0] ?? "Something went wrong";
  } catch (err) {
    return "Something went wrong";
  }
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    width: 100%;
  `,
  compactRow: css`
    flex-wrap: nowrap;
  `,
  action: css`
    margin-left: auto;
    white-space: nowrap;
  `,
  failure: css`
    flex-basis: 100%;
    color: ${Colors.ERROR};
    font-size: 0.75rem;
    line-height: 1.3;
  `,
  chipButton: css`
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
  `,
};
